
import React from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import DashboardLayout from '@/components/DashboardLayout';
import PageCallToAction from '@/components/PageCallToAction';

const Security = () => {
  return (
    <DashboardLayout>
      <div className="container mx-auto py-12 px-4">
        <Helmet>
          <title>Information Security | OpenPipe</title>
        </Helmet>
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="text-3xl font-bold">Information Security</CardTitle>
            <CardDescription>Last updated February 27, 2024</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 legal-page">
            <div>
              <p className="mb-4">
                This Information Security Policy outlines the framework for managing OpenPipe, Inc.'s information security. 
                Our commitment is to protect the confidentiality, integrity, and availability of data entrusted to us by our 
                customers, especially focusing on our model fine-tuning service. Through this policy, we establish and 
                communicate our information security objectives and procedures to all employees, contractors, and third 
                parties involved with our information systems and processes.
              </p>
            </div>
            
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">2. Purpose</h2>
              <p className="mb-4">The purpose of this policy is to ensure that:</p>
              <ul className="list-disc ml-8 mb-4">
                <li className="mb-2">Customer data is protected against unauthorized access, disclosure, alteration, and destruction.</li>
                <li className="mb-2">Our information processing facilities and IT infrastructure are secure.</li>
                <li className="mb-2">Information security is integral to our corporate culture.</li>
                <li className="mb-2">Legal, regulatory, and contractual obligations are met.</li>
              </ul>
            </div>
            
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">3. Scope</h2>
              <p className="mb-4">
                This policy applies to all employees, contractors, and third-party users of information systems 
                and IT infrastructure owned, operated, or provided by OpenPipe, Inc. It covers all forms of data, 
                including electronic and physical, information systems, and data centers.
              </p>
            </div>
            
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">4. Data Classification and Handling</h2>
              <ul className="mb-4">
                <li className="mb-2">
                  <strong>Data Encryption:</strong> All customer data is encrypted at rest using industry-standard 
                  encryption technologies. Our storage solution, AWS RDS, is configured to ensure the highest levels 
                  of data protection.
                </li>
                <li className="mb-2">
                  <strong>Data Usage:</strong> Customer data is exclusively used for the purpose of training fine-tuned 
                  models within their own customer account. Customers must provide explicit consent for any use beyond this scope.
                </li>
              </ul>
            </div>
            
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">5. Access Control</h2>
              <ul className="mb-4">
                <li className="mb-2">
                  Access to customer data and sensitive information systems is restricted to authorized personnel only, 
                  based on the principle of least privilege and role-based access control.
                </li>
                <li className="mb-2">
                  Multi-factor authentication and strong password policies are enforced for all access to systems handling 
                  sensitive information.
                </li>
              </ul>
            </div>
            
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">6. Physical and Network Security</h2>
              <ul className="mb-4">
                <li className="mb-2">
                  Our physical infrastructure is hosted in secure AWS facilities that comply with SOC 2 standards, 
                  ensuring robust physical security controls.
                </li>
                <li className="mb-2">
                  Network security measures, including firewalls and intrusion detection systems, are implemented to 
                  protect against unauthorized access and cyber threats.
                </li>
              </ul>
            </div>
            
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">7. Third-party Security</h2>
              <ul className="mb-4">
                <li className="mb-2">
                  We engage with third-party services for data processing and inference serving. All third parties are 
                  vetted to ensure they are SOC 2 compliant and adhere to strict data usage policies that align with this policy.
                </li>
              </ul>
            </div>
            
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">8. Incident Response and Management</h2>
              <ul className="mb-4">
                <li className="mb-2">
                  We have established an incident response plan to quickly address any security breaches or incidents, 
                  minimizing impact and ensuring timely communication with affected parties.
                </li>
              </ul>
            </div>
            
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">9. Compliance and Legal Requirements</h2>
              <ul className="mb-4">
                <li className="mb-2">
                  OpenPipe, Inc. is committed to complying with all relevant legal, regulatory, and contractual obligations, 
                  including data protection laws and privacy requirements.
                </li>
              </ul>
            </div>
            
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">10. Policy Review and Enforcement</h2>
              <ul className="mb-4">
                <li className="mb-2">
                  This policy will be reviewed at least annually or following significant changes to our business, 
                  technology, or threat landscape.
                </li>
                <li className="mb-2">
                  Violations of this policy may result in disciplinary action, up to and including termination of 
                  employment or contracts, and legal action.
                </li>
              </ul>
            </div>
            
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">11. Contact Information</h2>
              <p className="mb-4">
                For questions or concerns regarding this policy or information security practices at OpenPipe, Inc., 
                please contact our Information Security Officer at <a href="mailto:security@openpipe.com">security@openpipe.com</a>.
              </p>
            </div>
          </CardContent>
        </Card>
        
        <div className="mt-12">
          <PageCallToAction />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Security;
